import * as React from "react";
import * as __intl__ from "@frameio/i18n";
export type Text = {
    enterpriseAddOn: () => string | React.ReactNode;
    availableWithEnterprise: () => string | React.ReactNode;
    availableWithPlan: (planTitle: string) => string | React.ReactNode;
    availableWithNoPlan: () => string | React.ReactNode;
    shareTooltipTitle: (feature: string) => string | React.ReactNode;
    featureDisplayNames: {
        /** Email domain restrictions for account access */
        allowedDomains: string;
        /** Long-term storage for inactive content */
        archivalStorage: string;
        /** Automated content lifecycle policies */
        assetLifecycleManagement: string;
        /** Ability to attach files to comments */
        commentAttachments: string;
        /** Custom email branding for notifications */
        customBrandedEmails: string;
        /** Custom branding for presentation mode */
        customBrandedPresentations: string;
        /** Custom branding for share links */
        customBrandedShares: string;
        /** Custom ordering of assets in projects */
        customSort: string;
        /** Desktop and mobile device integrations */
        devices: string;
        /** Restrict team member invitation permissions */
        disableTeamMemberInvites: string;
        /** Digital rights management protection */
        drm: string;
        /** Unique watermarking for forensic tracking */
        forensicWatermarking: string;
        /** 4K ultra-high definition video playback */
        fourKPlayback: string;
        /** Generic fallback used when a plan feature has no specific display name yet */
        genericFeature: string;
        /** High dynamic range video support */
        hdr: string;
        /** External storage mounting capability */
        mountedStorage: string;
        /** Allow storage usage beyond plan limits */
        mountedStorageOverprovisioningAllowed: string;
        /** Natural language search for content */
        naturalLanguageSearch: string;
        /** Passphrase protection for share links */
        passwordProtectedShares: string;
        /** Projects with restricted access controls */
        privateProjects: string;
        /** Reel-style layout for video review */
        reelPlayer: string;
        /** Content security policy management */
        resourceControlPolicies: string;
        /** Folders with restricted permissions */
        restrictedFolders: string;
        /** Advanced share link security features */
        secureSharing: string;
        /** AI-powered semantic content search */
        semanticSearch: string;
        /** Session-specific watermarking */
        sessionBasedWatermarking: string;
        /** Time-limited share link access */
        shareLinkExpiration: string;
        /** Share link permission controls */
        shareLinkSecurity: string;
        /** Share lists feature */
        shareLists: string;
        /** Static watermark overlays */
        staticWatermark: string;
        /** Connect external storage systems */
        storageConnect: string;
        /** Internal-only commenting feature */
        teamOnlyComments: string;
        /** Automatic video transcription */
        transcription: string;
        /** Unlimited custom watermark templates */
        unlimitedWatermarkTemplates: string;
        /** Shareable links for unauthenticated uploads to a folder */
        uploadLinks: string;
        /** User group management feature */
        userGroups: string;
        /** Advanced user permission management */
        userManagement: string;
    };
};
export type Deps = {
    TooltipTitle: any;
    TooltipDetails: any;
};
export default {
    DEFINE(_messages: Text, { TooltipTitle, TooltipDetails }: Deps): void {
        return Object.defineProperties(this, {
            /** Tooltip for Enterprise subscribers viewing add-on features */
            enterpriseAddOn: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => () => __intl__.intl().formatMessage({ id: "FeatureGateTooltipTitle.enterpriseAddOn?c=361nno0jh606g", defaultMessage: "<jsx><TooltipTitle>This feature is an Enterprise add-on.</TooltipTitle> <TooltipDetails>Click to see details.</TooltipDetails></jsx>" }, { jsx: (__children__: React.ReactNode) => React.Children.toArray(__children__), TooltipTitle: (__children__: React.ReactNode) => <TooltipTitle>{React.Children.toArray(__children__)}</TooltipTitle>, TooltipDetails: (__children__: React.ReactNode) => <TooltipDetails>{React.Children.toArray(__children__)}</TooltipDetails> }));
                },
                enumerable: true,
                configurable: true
            },
            /** Tooltip for Enterprise plan upgrade */
            availableWithEnterprise: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => () => __intl__.intl().formatMessage({ id: "FeatureGateTooltipTitle.availableWithEnterprise?c=1bedjwji8lqa3", defaultMessage: "<jsx><TooltipTitle>This feature is only available with an  <strong>Enterprise plan</strong>.</TooltipTitle> <TooltipDetails>Click to upgrade.</TooltipDetails></jsx>" }, { jsx: (__children__: React.ReactNode) => React.Children.toArray(__children__), TooltipTitle: (__children__: React.ReactNode) => <TooltipTitle>{React.Children.toArray(__children__)}</TooltipTitle>, strong: (__children__: React.ReactNode) => <strong>{React.Children.toArray(__children__)}</strong>, TooltipDetails: (__children__: React.ReactNode) => <TooltipDetails>{React.Children.toArray(__children__)}</TooltipDetails> }));
                },
                enumerable: true,
                configurable: true
            },
            /** Tooltip for other plan upgrades (Pro, Team, etc.) */
            availableWithPlan: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (planTitle: string) => __intl__.intl().formatMessage({ id: "FeatureGateTooltipTitle.availableWithPlan?c=ewq1tugc2ks7", defaultMessage: "<jsx><TooltipTitle>This feature is only available with a  <strong>{planTitle} plan</strong> or above.</TooltipTitle> <TooltipDetails>Click to upgrade.</TooltipDetails></jsx>" }, { jsx: (__children__: React.ReactNode) => React.Children.toArray(__children__), TooltipTitle: (__children__: React.ReactNode) => <TooltipTitle>{React.Children.toArray(__children__)}</TooltipTitle>, strong: (__children__: React.ReactNode) => <strong>{React.Children.toArray(__children__)}</strong>, planTitle: planTitle, TooltipDetails: (__children__: React.ReactNode) => <TooltipDetails>{React.Children.toArray(__children__)}</TooltipDetails> }));
                },
                enumerable: true,
                configurable: true
            },
            /** Tooltip when no plan title (edge case) */
            availableWithNoPlan: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => () => __intl__.intl().formatMessage({ id: "FeatureGateTooltipTitle.availableWithNoPlan?c=1arjf30177oyj", defaultMessage: "<jsx><TooltipDetails>Click to upgrade.</TooltipDetails></jsx>" }, { jsx: (__children__: React.ReactNode) => React.Children.toArray(__children__), TooltipDetails: (__children__: React.ReactNode) => <TooltipDetails>{React.Children.toArray(__children__)}</TooltipDetails> }));
                },
                enumerable: true,
                configurable: true
            },
            /** Tooltip for public share views */
            shareTooltipTitle: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (feature: string) => __intl__.intl().formatMessage({ id: "FeatureGateTooltipTitle.shareTooltipTitle?c=1z8rpvfjgxaak", defaultMessage: "<TooltipTitle>{feature} not available on this account</TooltipTitle>" }, { TooltipTitle: (__children__: React.ReactNode) => <TooltipTitle>{React.Children.toArray(__children__)}</TooltipTitle>, feature: feature }));
                },
                enumerable: true,
                configurable: true
            },
            /** Feature display names used in public share view tooltips */
            featureDisplayNames: {
                get() {
                    return Object.defineProperties({
                        [__intl__.BOUND_INTL]: this[__intl__.BOUND_INTL] ?? __intl__.getIntl()
                    }, {
                        /** Email domain restrictions for account access */
                        allowedDomains: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FeatureGateTooltipTitle.featureDisplayNames.allowedDomains?c=nzqglxvqkbou", defaultMessage: "Allowed domains are" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Long-term storage for inactive content */
                        archivalStorage: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FeatureGateTooltipTitle.featureDisplayNames.archivalStorage?c=29wl3yozoas30", defaultMessage: "Archival storage is" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Automated content lifecycle policies */
                        assetLifecycleManagement: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FeatureGateTooltipTitle.featureDisplayNames.assetLifecycleManagement?c=3e8tltq9t6xn4", defaultMessage: "Asset lifecycle management is" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Ability to attach files to comments */
                        commentAttachments: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FeatureGateTooltipTitle.featureDisplayNames.commentAttachments?c=2jx0iui3n90cf", defaultMessage: "Comment attachments are" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Custom email branding for notifications */
                        customBrandedEmails: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FeatureGateTooltipTitle.featureDisplayNames.customBrandedEmails?c=fk40m4lhuqvj", defaultMessage: "Custom-branded emails are" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Custom branding for presentation mode */
                        customBrandedPresentations: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FeatureGateTooltipTitle.featureDisplayNames.customBrandedPresentations?c=3b24j9n6cgepq", defaultMessage: "Custom-branded presentations are" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Custom branding for share links */
                        customBrandedShares: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FeatureGateTooltipTitle.featureDisplayNames.customBrandedShares?c=kjoscmablgsj", defaultMessage: "Custom-branded shares are" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Custom ordering of assets in projects */
                        customSort: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FeatureGateTooltipTitle.featureDisplayNames.customSort?c=19xtyi4thbk79", defaultMessage: "Custom sort is" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Desktop and mobile device integrations */
                        devices: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FeatureGateTooltipTitle.featureDisplayNames.devices?c=24g37j2hj04rx", defaultMessage: "Devices are" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Restrict team member invitation permissions */
                        disableTeamMemberInvites: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FeatureGateTooltipTitle.featureDisplayNames.disableTeamMemberInvites?c=32phdbi8nuqys", defaultMessage: "Disabling team member invites is" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Digital rights management protection */
                        drm: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FeatureGateTooltipTitle.featureDisplayNames.drm?c=22ganihvlpde9", defaultMessage: "DRM is" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Unique watermarking for forensic tracking */
                        forensicWatermarking: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FeatureGateTooltipTitle.featureDisplayNames.forensicWatermarking?c=3tbzt3fao3ffy", defaultMessage: "Forensic watermarking is" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** 4K ultra-high definition video playback */
                        fourKPlayback: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FeatureGateTooltipTitle.featureDisplayNames.fourKPlayback?c=15f1nn2fzhs5o", defaultMessage: "4K UHD playback is" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Generic fallback used when a plan feature has no specific display name yet */
                        genericFeature: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FeatureGateTooltipTitle.featureDisplayNames.genericFeature?c=2suplgoy8o2wr", defaultMessage: "This feature is" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** High dynamic range video support */
                        hdr: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FeatureGateTooltipTitle.featureDisplayNames.hdr?c=3mbilic4gokc9", defaultMessage: "HDR is" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** External storage mounting capability */
                        mountedStorage: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FeatureGateTooltipTitle.featureDisplayNames.mountedStorage?c=2483rv3gz9029", defaultMessage: "Mounted storage is" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Allow storage usage beyond plan limits */
                        mountedStorageOverprovisioningAllowed: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FeatureGateTooltipTitle.featureDisplayNames.mountedStorageOverprovisioningAllowed?c=6hbn3kyxw247", defaultMessage: "Mounted storage overprovisioning is" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Natural language search for content */
                        naturalLanguageSearch: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FeatureGateTooltipTitle.featureDisplayNames.naturalLanguageSearch?c=17bmkh4fdmzvv", defaultMessage: "Natural language search is" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Passphrase protection for share links */
                        passwordProtectedShares: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FeatureGateTooltipTitle.featureDisplayNames.passwordProtectedShares?c=ih0ao8xhune4", defaultMessage: "Passphrase-protected shares are" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Projects with restricted access controls */
                        privateProjects: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FeatureGateTooltipTitle.featureDisplayNames.privateProjects?c=21x5nl1vn1d0", defaultMessage: "Restricted projects are" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Reel-style layout for video review */
                        reelPlayer: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FeatureGateTooltipTitle.featureDisplayNames.reelPlayer?c=2t4vudgvq1u5r", defaultMessage: "Reel layout is" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Content security policy management */
                        resourceControlPolicies: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FeatureGateTooltipTitle.featureDisplayNames.resourceControlPolicies?c=2w9foon38pfni", defaultMessage: "Content security settings are" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Folders with restricted permissions */
                        restrictedFolders: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FeatureGateTooltipTitle.featureDisplayNames.restrictedFolders?c=1mwicu93kmx5c", defaultMessage: "Restricted folders are" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Advanced share link security features */
                        secureSharing: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FeatureGateTooltipTitle.featureDisplayNames.secureSharing?c=1cyw3lozxwzhv", defaultMessage: "Secure sharing is" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** AI-powered semantic content search */
                        semanticSearch: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FeatureGateTooltipTitle.featureDisplayNames.semanticSearch?c=2zrcdbt0cdu53", defaultMessage: "Semantic search is" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Session-specific watermarking */
                        sessionBasedWatermarking: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FeatureGateTooltipTitle.featureDisplayNames.sessionBasedWatermarking?c=yxbz5xbdcx3n", defaultMessage: "Session-based watermarking is" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Time-limited share link access */
                        shareLinkExpiration: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FeatureGateTooltipTitle.featureDisplayNames.shareLinkExpiration?c=2ajd5knxiedho", defaultMessage: "Share link expiration is" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Share link permission controls */
                        shareLinkSecurity: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FeatureGateTooltipTitle.featureDisplayNames.shareLinkSecurity?c=31rj2kb4xpuw5", defaultMessage: "Share link security & permissions are" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Share lists feature */
                        shareLists: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FeatureGateTooltipTitle.featureDisplayNames.shareLists?c=2rnyz4mn6x7xu", defaultMessage: "Share lists are" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Static watermark overlays */
                        staticWatermark: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FeatureGateTooltipTitle.featureDisplayNames.staticWatermark?c=2byozmj0p0ru7", defaultMessage: "Static watermark is" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Connect external storage systems */
                        storageConnect: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FeatureGateTooltipTitle.featureDisplayNames.storageConnect?c=bum8o2p0g5w5", defaultMessage: "Storage connect is" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Internal-only commenting feature */
                        teamOnlyComments: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FeatureGateTooltipTitle.featureDisplayNames.teamOnlyComments?c=21cvmk9z16zfu", defaultMessage: "Internal comments are" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Automatic video transcription */
                        transcription: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FeatureGateTooltipTitle.featureDisplayNames.transcription?c=39ol8dt0kx9xj", defaultMessage: "Transcription is" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Unlimited custom watermark templates */
                        unlimitedWatermarkTemplates: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FeatureGateTooltipTitle.featureDisplayNames.unlimitedWatermarkTemplates?c=2g43tl5w2rzv1", defaultMessage: "Unlimited watermark templates are" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Shareable links for unauthenticated uploads to a folder */
                        uploadLinks: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FeatureGateTooltipTitle.featureDisplayNames.uploadLinks?c=2nlv819qgwhsb", defaultMessage: "Upload links are" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** User group management feature */
                        userGroups: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FeatureGateTooltipTitle.featureDisplayNames.userGroups?c=1e87vnnlhifv7", defaultMessage: "User groups are" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Advanced user permission management */
                        userManagement: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FeatureGateTooltipTitle.featureDisplayNames.userManagement?c=vd7foxxudbin", defaultMessage: "User management is" }));
                            },
                            enumerable: true,
                            configurable: true
                        }
                    });
                },
                enumerable: true,
                configurable: true
            }
        });
    }
} as Text & {
    DEFINE(messages: Text, deps: Deps): void;
};
